'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import VideoIcon from '@/components/assets/icon_trailer_video.svg';
import OptimizedImage from '@/components/OptimizedImage';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { TrailerViewMediaProvider, useTrailerViewMediaContext } from '@/context/TrailerViewMediaContext';
import { MEDIA_TYPE } from '@/hooks/useTrailerViewMedia';
import { cn } from '@/lib/utils';
import { Asset, TrailerViewVideoData } from '@/types/asset';
const Lightbox = dynamic(() => import('@/app/[locale]/(worlds)/profile/[identifier]/components/Lightbox'), {
  ssr: false
});
function toAssets(mediaList: ReturnType<typeof useTrailerViewMediaContext>['mediaList']): Asset[] {
  return mediaList.map(mediaItem => {
    if (mediaItem.type === MEDIA_TYPE.IMAGE) {
      return {
        image: {
          url: mediaItem.url
        }
      };
    }
    const videoData = mediaItem as TrailerViewVideoData;
    return {
      video: {
        ...videoData,
        isFetching: false
      }
    };
  });
}
function MobileTrailerThumbnailsInner() {
  const $ = _c(20);
  const {
    mediaList
  } = useTrailerViewMediaContext();
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  if (mediaList.length <= 1) {
    return null;
  }
  let t0;
  if ($[0] !== mediaList) {
    t0 = toAssets(mediaList);
    $[0] = mediaList;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const assets = t0;
  let t1;
  if ($[2] !== isOpen || $[3] !== lightboxIndex || $[4] !== mediaList) {
    let t2;
    if ($[6] !== isOpen || $[7] !== lightboxIndex) {
      t2 = (mediaItem, index) => {
        const isVideo = mediaItem.type === MEDIA_TYPE.VIDEO;
        const coverSrc = isVideo ? (mediaItem as TrailerViewVideoData).coverImage : mediaItem.url;
        return <div key={index} className={cn("relative aspect-video w-[206px] shrink-0 cursor-pointer overflow-hidden rounded-lg", lightboxIndex === index && isOpen && "ring-vive-brand-50 ring-[3px]")} onClick={() => {
          setLightboxIndex(index);
          setIsOpen(true);
        }}>{coverSrc ? <OptimizedImage src={coverSrc} alt={`Trailer ${index + 1}`} fill={true} sizes="206px" className="object-cover" /> : <div className="bg-vive-surface-normal size-full" />}{isVideo && <VideoIcon className="pointer-events-none absolute bottom-[5px] left-1.5 size-3.5" />}</div>;
      };
      $[6] = isOpen;
      $[7] = lightboxIndex;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = mediaList.map(t2);
    $[2] = isOpen;
    $[3] = lightboxIndex;
    $[4] = mediaList;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = <div className="no-scrollbar mb-6 flex w-full gap-3 overflow-x-auto px-[3px] py-[3px]">{t1}</div>;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== assets || $[12] !== isOpen || $[13] !== lightboxIndex) {
    t3 = isOpen && <Lightbox assets={assets} selectedAssetIndex={lightboxIndex} setIsLightboxOpen={setIsOpen} setSelectedAssetIndex={setLightboxIndex} share={false} />;
    $[11] = assets;
    $[12] = isOpen;
    $[13] = lightboxIndex;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== t3) {
    t4 = <AnimatePresence>{t3}</AnimatePresence>;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t2 || $[18] !== t4) {
    t5 = <>{t2}{t4}</>;
    $[17] = t2;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
export default function MobileTrailerMediaSection() {
  const $ = _c(1);
  const {
    isTrailer,
    shouldShowMobileTip,
    shouldShowTrailerThumbnails
  } = useCheckEnterRoomContext();
  if (!isTrailer && !shouldShowMobileTip && !shouldShowTrailerThumbnails) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <TrailerViewMediaProvider sendBI={false}><MobileTrailerThumbnailsInner /></TrailerViewMediaProvider>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}